import { UserAvatar } from '@/components/user-avatar'
import { Button } from '@journ/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@journ/ui/dropdown-menu'
import { Icons } from '@journ/ui/icons'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@journ/ui/select'
import { Sheet, SheetContent, SheetHeader } from '@journ/ui/sheet'
import { SubmitButton } from '@journ/ui/submit-button'
import { Table, TableBody, TableCell, TableRow } from '@journ/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@journ/ui/tooltip'
import { useState } from 'react'
import { useSelector } from '@/libs/state'
import { useIntlayer } from 'react-intlayer'
import type { CompanyRole } from '@/features/permissions/domain/permission'
import { createUser } from '../application/create-user'
import { updateUserRole } from '../application/update-user-role'
import { users as usersPort, notify, settingsUsers$ } from '../wiring'
import { addMemberOpen$ } from '../store'
import type { User } from '../domain/user'

const ROLE_ORDER: CompanyRole[] = ['owner', 'manager', 'employee']

const MemberRow = ({ user }: { user: User }) => {
	const content = useIntlayer('settings-members-page')
	const [changing, setChanging] = useState(false)

	const handleRoleChange = async (role: string) => {
		setChanging(true)
		const updated = await updateUserRole(usersPort, user.id, role as CompanyRole, notify)
		settingsUsers$.set(prev => prev.map(u => (u.id === user.id ? updated : u)))
		setChanging(false)
	}

	return (
		<TableRow className='hover:bg-transparent'>
			<TableCell className='py-4 border-r-0'>
				<div className='flex items-center space-x-4'>
					<UserAvatar name={user.fullName} avatarUrl={user.avatarUrl} size='md' />
					<div className='flex flex-col'>
						<span className='font-medium text-sm'>{user.fullName}</span>
						<span className='text-sm text-606060'>{user.email}</span>
					</div>
				</div>
			</TableCell>

			<TableCell className='text-right border-r-0'>
				<div className='flex justify-end items-center space-x-2'>
					{user.companyRole === 'owner' ? (
						<span className='text-sm text-606060'>{content.roles.owner}</span>
					) : (
						<Select value={user.companyRole} onValueChange={handleRoleChange} disabled={changing}>
							<SelectTrigger className='h-8 w-32 text-sm'>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='manager'>{content.roleOptions.manager}</SelectItem>
								<SelectItem value='employee'>{content.roleOptions.employee}</SelectItem>
							</SelectContent>
						</Select>
					)}

					{user.companyRole !== 'owner' && (
						<TooltipProvider>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant='ghost' className='h-8 w-8 p-0'>
										<Icons.MoreHoriz className='h-4 w-4' />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align='end'>
									<Tooltip>
										<TooltipTrigger asChild>
											<DropdownMenuItem
												disabled
												className='text-destructive focus:text-destructive opacity-40 cursor-not-allowed'
											>
												{content.remove}
											</DropdownMenuItem>
										</TooltipTrigger>
										<TooltipContent side='left'>{content.comingSoon}</TooltipContent>
									</Tooltip>
								</DropdownMenuContent>
							</DropdownMenu>
						</TooltipProvider>
					)}
				</div>
			</TableCell>
		</TableRow>
	)
}

type AddMemberFormValues = {
	fullName: string
	email: string
	companyRole: CompanyRole
}

const AddMemberSheet = () => {
	const content = useIntlayer('settings-members-page')
	const open = useSelector(addMemberOpen$)
	const [form, setForm] = useState<AddMemberFormValues>({ fullName: '', email: '', companyRole: 'employee' })
	const [errors, setErrors] = useState<Partial<AddMemberFormValues>>({})
	const [submitting, setSubmitting] = useState(false)

	const close = () => addMemberOpen$.set(false)

	const validate = (): boolean => {
		const next: Partial<AddMemberFormValues> = {}
		if (!form.fullName.trim()) next.fullName = 'Required'
		if (!form.email.trim()) {
			next.email = 'Required'
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
			next.email = 'Invalid email'
		}
		setErrors(next)
		return Object.keys(next).length === 0
	}

	const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>): Promise<void> => {
		e.preventDefault()
		if (!validate()) return
		setSubmitting(true)
		try {
			const user = await createUser(usersPort, form, notify)
			settingsUsers$.set(prev => [...prev, user])
			close()
			setForm({ fullName: '', email: '', companyRole: 'employee' })
			setErrors({})
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Sheet open={open} onOpenChange={close}>
			<SheetContent stack title={content.addSheet.title.value}>
				<SheetHeader className='mb-6 flex justify-between items-center flex-row'>
					<h2 className='text-xl'>{content.addSheet.title}</h2>
					<Button size='icon' variant='ghost' onClick={close} className='p-0 m-0 size-auto hover:bg-transparent'>
						<Icons.Close className='size-5' />
					</Button>
				</SheetHeader>

				<form onSubmit={handleSubmit} className='flex flex-col gap-4'>
					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='fullName'>{content.addSheet.fullName}</Label>
						<Input
							id='fullName'
							value={form.fullName}
							onChange={e => setForm(prev => ({ ...prev, fullName: e.target.value }))}
							autoComplete='off'
							autoCapitalize='words'
							autoCorrect='off'
							spellCheck={false}
						/>
						{errors.fullName && <p className='text-xs text-destructive'>{errors.fullName}</p>}
					</div>

					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='email'>{content.addSheet.email}</Label>
						<Input
							id='email'
							type='email'
							value={form.email}
							onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
							autoComplete='off'
							autoCapitalize='none'
							autoCorrect='off'
							spellCheck={false}
						/>
						{errors.email && <p className='text-xs text-destructive'>{errors.email}</p>}
					</div>

					<div className='flex flex-col gap-1.5'>
						<Label htmlFor='companyRole'>{content.addSheet.role}</Label>
						<Select
							value={form.companyRole}
							onValueChange={(v: string) => setForm(prev => ({ ...prev, companyRole: v as CompanyRole }))}
						>
							<SelectTrigger id='companyRole'>
								<SelectValue placeholder={content.addSheet.selectRole.value} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='manager'>{content.roleOptions.manager}</SelectItem>
								<SelectItem value='employee'>{content.roleOptions.employee}</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<div className='flex justify-end gap-2 pt-2'>
						<Button type='button' variant='outline' onClick={close}>
							{content.addSheet.cancel}
						</Button>
						<SubmitButton type='submit' isSubmitting={submitting}>
							{content.addSheet.submit}
						</SubmitButton>
					</div>
				</form>
			</SheetContent>
		</Sheet>
	)
}

const SettingsMembersPage = () => {
	const content = useIntlayer('settings-members-page')
	const users = useSelector(settingsUsers$)

	const sorted = [...users].sort((a, b) => ROLE_ORDER.indexOf(a.companyRole) - ROLE_ORDER.indexOf(b.companyRole))

	return (
		<div className='space-y-6 mt-4'>
			<div className='flex items-center space-x-4 justify-end'>
				<Button onClick={() => addMemberOpen$.set(true)}>{content.addMember}</Button>
			</div>

			<Table>
				<TableBody>
					{sorted.map(user => (
						<MemberRow key={user.id} user={user} />
					))}
				</TableBody>
			</Table>

			<AddMemberSheet />
		</div>
	)
}

export { SettingsMembersPage }
